type Location = {
  lat: number
  lng: number
}

type AddressComponent = {
  name: string
  type: string
}

type AddressSuggestion = {
  id: string
  displayText: string
  components: AddressComponent[]
  location?: Location
}

// API response types
type ApiSuggestion = {
  title: {
    text: string
    hl?: Array<{ begin: number; end: number }>
  }
  subtitle?: {
    text: string
  }
  tags: Array<string>
  distance?: {
    text: string
    value: number
  }
  address?: {
    formatted_address: string
    component: Array<{
      name: string
      kind: Array<string>
    }>
  }
  uri?: string
}

type ApiResponse = {
  results: Array<ApiSuggestion>
}

// Convert API response to our format
const convertToAddressSuggestion = (
  apiSuggestion: ApiSuggestion,
  index: number,
): AddressSuggestion => {
  const components = apiSuggestion.address?.component || []

  return {
    id: apiSuggestion.uri || `suggestion_${index}`,
    displayText:
      apiSuggestion.address?.formatted_address || apiSuggestion.title.text,
    components: components.map((component) => ({
      name: component.name,
      type: component.kind[0]?.toLowerCase() || 'unknown',
    })),
    location: undefined, // Location data not provided by this API
  }
}

export const getSuggestions = async (
  address: string,
  sessionToken: string,
  options: Record<string, any> = {},
): Promise<AddressSuggestion[]> => {
  try {
    const apiKey = process.env.NEXT_PUBLIC_YANDEX_GEOSUGGEST_API_KEY

    if (!apiKey) {
      throw new Error('Geosuggest API key is not configured')
    }

    // Build query parameters
    const params = new URLSearchParams({
      apikey: apiKey,
      text: address,
      sessiontoken: sessionToken,
      lang: 'ru',
      results: '10',
      print_address: '1',
      attrs: 'uri',
    })

    // Handle country restrictions
    if (options.componentRestrictions?.country) {
      const countryCode = options.componentRestrictions.country.toLowerCase()

      const countryConfig: Record<string, { bbox: string; name: string }> = {
        kz: {
          bbox: '46.4932,40.5686,87.3156,55.4421',
          name: 'Kazakhstan',
        },
        kg: {
          bbox: '69.2649,39.1728,80.2599,43.2983',
          name: 'Kyrgyzstan',
        },
      }

      const config = countryConfig[countryCode]
      if (config) {
        params.set('bbox', config.bbox)
        params.set('strict_bounds', '1')

        const currentText = params.get('text') || ''
        params.set('text', `${currentText}, ${config.name}`)
      }
    }

    const response = await fetch(
      `https://suggest-maps.yandex.ru/v1/suggest?${params.toString()}`,
    )

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`API error: ${response.status} - ${errorText}`)
    }

    const data: ApiResponse = await response.json()

    // Convert to our format
    const suggestions = (data.results ?? []).map(convertToAddressSuggestion)

    return suggestions
  } catch (error) {
    console.error('Error fetching suggestions:', error)
    throw error
  }
}
